import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, Home, CreditCard } from 'lucide-react';
import api from '../services/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await api.get(`/checkout/status/${sessionId}`);
      const { payment_status, status: sessionStatus, amount_total, currency } = response.data;

      setPaymentInfo({ amount: amount_total / 100, currency });

      if (payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (sessionStatus === 'expired') {
        setStatus('expired');
        return;
      }

      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
      case 'processing':
        return (
          <div className="text-center py-12">
            <Loader2 className="w-16 h-16 mx-auto text-blue-600 animate-spin mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Processing Your Payment
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we confirm your payment...
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Payment Successful!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Thank you for your purchase. Your subscription is now active.
            </p>
            {paymentInfo && (
              <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400 mb-8">
                Amount paid: ${paymentInfo.amount.toFixed(2)} {paymentInfo.currency?.toUpperCase()}
              </p>
            )}
            <div className="flex justify-center space-x-4">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
              <Button
                onClick={() => navigate('/pricing')}
                variant="outline"
                className="dark:border-gray-600 dark:text-gray-300"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                View Subscription
              </Button>
            </div>
          </div>
        );

      case 'expired':
      case 'timeout':
      case 'error':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center mb-6">
              <XCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {status === 'expired' ? 'Payment Session Expired' : 'Payment Verification Failed'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t verify your payment. Please check your email for confirmation or contact support.'}
            </p>
            <div className="flex justify-center space-x-4">
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="dark:border-gray-600 dark:text-gray-300"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card className="shadow-lg border-0 dark:bg-gray-800">
        <CardHeader className="border-b dark:border-gray-700">
          <CardTitle className="text-center dark:text-white">Payment Status</CardTitle>
        </CardHeader>
        <CardContent>
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
